import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useSocket } from '../../../context/SocketContext';
import { doctorAPI, consultationAPI, earningsAPI, default as api } from '../../../utils/api';

const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [stats, setStats] = useState({
    totalPatients: 0,
    todayAppointments: 0,
    pendingConsultations: 0,
    earnings: 0
  });
  const [appointments, setAppointments] = useState([]);
  const [pendingPrescriptions, setPendingPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('');

  const { socket } = useSocket();

  useEffect(() => {
    fetchDashboardData();

    if (socket) {
      socket.on('new_consultation', (data) => {
        showSuccess(`New session request from ${data.patient_id?.name || 'User'}`);
        fetchDashboardData();
      });

      socket.on('consultation_updated', () => {
        fetchDashboardData();
      });

      return () => {
        socket.off('new_consultation');
        socket.off('consultation_updated');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const fetchDashboardData = async () => {
    // Skip if user is not approved or missing profile to prevent errors
    if (!user || !user.is_approved) {
      setLoading(false);
      return;
    }

    try {
      const [statsRes, appointmentsRes, pendingRes] = await Promise.all([
        doctorAPI.getStats(),
        doctorAPI.getAppointments(),
        earningsAPI.getPendingEarnings()
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data || stats);
      }

      if (appointmentsRes.data?.success) {
        setAppointments(appointmentsRes.data.data || []);
      }

      if (pendingRes.data?.success) {
        setPendingPrescriptions(pendingRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Only show error if user is actually approved, otherwise silence it
      if (user?.is_approved) {
        showError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptClick = (appointment) => {
    if (appointment.consultation_type === 'chat') {
      acceptConsultation(appointment._id);
    } else {
      setSelectedConsultation(appointment);
      setShowScheduleModal(true);
    }
  };

  const acceptConsultation = async (consultationId, scheduledAt = null) => {
    try {
      const data = scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {};
      const res = await api.post(`/consultation/${consultationId}/accept`, data);
      if (res.data?.success) {
        showSuccess('Consultation accepted successfully');
        setShowScheduleModal(false);
        setScheduleTime('');
        setSelectedConsultation(null);
        fetchDashboardData(); // Refresh data
      } else {
        showError(res.data?.message || 'Failed to accept consultation');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to accept consultation');
    }
  };

  const rejectConsultation = async (consultationId) => {
    if (!window.confirm('Are you sure you want to decline this consultation?')) return;
    try {
      const res = await consultationAPI.reject(consultationId, { reason: 'rejected' });
      if (res.data?.success) {
        showSuccess('Consultation declined');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to decline consultation');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to decline consultation');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-yellow-500">Doctor Dashboard</h1>

      {/* Action Required: Pending Recommendations */}
      {pendingPrescriptions.length > 0 && (
        <div className="bg-red-900/20 border border-yellow-400 rounded-xl p-6 animate-pulse">
          <h2 className="text-xl font-semibold text-yellow-400 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Action Required: Pending Recommendations
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {pendingPrescriptions.map((item) => (
              <div key={item._id} className="bg-white/5 border border-white/10 backdrop-blur-xl p-4 rounded-xl flex justify-between items-center shadow-lg">
                <div>
                  <h3 className="font-medium text-white">User: {item.patientId?.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-400">Ended: {new Date(item.callEndTime).toLocaleString()}</p>
                </div>
                <button
                  onClick={() => navigate(`/prescription/create/${item.consultationId._id || item.consultationId}`)}
                  className="bg-yellow-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Create Recommendation
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Users</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.totalPatients}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Today's Sessions</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.todayAppointments}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Pending Sessions</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.pendingConsultations}</p>
        </div>
        {user?.role !== 'hospital_doctor' && (
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
            <h3 className="text-gray-400 text-sm mb-2">Earnings</h3>
            <p className="text-2xl font-bold text-yellow-500">₹{stats.earnings}</p>
          </div>
        )}
      </div>

      {/* Pending Consultations */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Pending Sessions</h2>
        <div className="space-y-4">
          {appointments.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No pending sessions</p>
            </div>
          ) : (
            appointments.map((appointment) => (
              <div key={appointment._id} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 backdrop-blur-lg rounded-xl">
                <div>
                  <h3 className="font-medium">User: {appointment.patient_id?.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-400">Type: {appointment.consultation_type}</p>
                  <p className="text-sm text-gray-400">Time: {appointment.scheduledAt ? new Date(appointment.scheduledAt).toLocaleString() : (appointment.createdAt ? new Date(appointment.createdAt).toLocaleString() : 'N/A')}</p>
                </div>
                <div className="flex space-x-2">
                  {/* Provide appropriate action based on status/type */}
                  {appointment.status === 'in_progress' || appointment.status === 'scheduled' || (appointment.consultation_type === 'chat' && appointment.status === 'requested') ? ( // Treat requested chat as actionable since it auto-accepts
                    <button
                      onClick={() => {
                        if (appointment.consultation_type === 'chat') {
                          navigate(`/chat/${appointment._id}`);
                        } else {
                          navigate(`/consultation/${appointment._id}`);
                        }
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      {appointment.consultation_type === 'chat' ? 'Open Chat' : 'Join Call'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleAcceptClick(appointment)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium"
                      >
                        Accept
                      </button>
                      <button onClick={() => rejectConsultation(appointment._id)} className="bg-yellow-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium">
                        Decline
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={() => navigate('/dashboard/doctor/consultation')} className="bg-yellow-500 text-dark py-3 px-4 rounded-lg font-medium hover:opacity-90">
            Schedule Session
          </button>
          <button onClick={() => setShowCalendarModal(true)} className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600">
            View Calendar
          </button>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-yellow-500 mb-4">Schedule Session</h3>
            <p className="text-gray-300 mb-4">
              Select a time for this {selectedConsultation?.consultation_type} session.
            </p>

            <input
              type="datetime-local"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-light mb-6"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 bg-gray-700 text-light py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => acceptConsultation(selectedConsultation._id, scheduleTime)}
                disabled={!scheduleTime}
                className="flex-1 bg-yellow-500 text-dark py-2 rounded-lg font-medium disabled:opacity-50"
              >
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Profile Image Warning (Softer if approved) */}
      {!user?.profile_image && (
        user?.is_approved ? (
          <div className="bg-yellow-900/20 border border-yellow-500 rounded-xl p-4 flex justify-between items-center">
            <div className="flex items-center">
              <span className="text-2xl mr-3">📸</span>
              <p className="text-yellow-200 text-sm">Your profile is missing a photo. Users feel more at ease when they can see you!</p>
            </div>
            <button onClick={() => navigate('/dashboard/doctor/profile')} className="text-yellow-500 font-bold text-sm underline">Update Now</button>
          </div>
        ) : (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-md w-full p-8 border border-yellow-400 text-center">
              <div className="w-20 h-20 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Action Required</h2>
              <p className="text-gray-300 mb-6">
                Welcome to Vivara! Before starting sessions, you <strong>must upload a profile picture</strong> for user identity verification.
              </p>
              <button
                onClick={() => navigate('/dashboard/doctor/profile')}
                className="bg-yellow-500 text-dark px-6 py-3 rounded-lg font-bold hover:bg-yellow-500 w-full"
              >
                Go to Profile to Upload
              </button>
            </div>
          </div>
        )
      )}

      {/* Calendar Modal */}
      {showCalendarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-yellow-500">Upcoming Appointments</h3>
              <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="overflow-y-auto flex-1 pr-2 space-y-4">
              {appointments.filter(a => a.status === 'scheduled' || a.status === 'accepted' || (a.status === 'in_progress' && a.consultation_type !== 'chat')).length === 0 ? (
                <div className="text-center py-8 text-gray-400">No upcoming scheduled appointments.</div>
              ) : (
                appointments
                  .filter(a => a.status === 'scheduled' || a.status === 'accepted' || (a.status === 'in_progress' && a.consultation_type !== 'chat'))
                  .sort((a, b) => new Date(a.scheduledAt || a.createdAt) - new Date(b.scheduledAt || b.createdAt))
                  .map((apt) => (
                    <div key={apt._id} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center border-l-4 border-yellow-500">
                      <div>
                        <h4 className="font-bold text-white">{apt.patient_id?.name || 'User'}</h4>
                        <p className="text-sm text-yellow-500">{apt.consultation_type.toUpperCase()} Session</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {apt.scheduledAt ? new Date(apt.scheduledAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Pending Time'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setShowCalendarModal(false);
                          navigate(`/consultation/${apt._id}`);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        Join Room
                      </button>
                    </div>
                  ))
              )}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-700 text-right">
              <button
                onClick={() => setShowCalendarModal(false)}
                className="bg-gray-700 hover:bg-gray-600 text-light py-2 px-6 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;
